/**
 * Sub Menu Navigation Handler
 * Smooth scroll to in-page targets; mobile dropdown reflects scroll position.
 */

class SubMenu extends HTMLElement {
  constructor() {
    super();
    this.links = this.querySelectorAll('.sub-menu-link');
    this.nav = this.querySelector('.sub-menu-nav');
    this.dropdown = this.querySelector('.sub-menu-select');
    this.dropdownValue = this.querySelector('.sub-menu-select-value');

    this.STICKY_OFFSET = 16;
    this.SCROLL_PADDING = 24;
    this.HYSTERESIS = 20;

    this.cachedHeaderHeight = null;
    this._scrollspyPaused = false;
    this._activeSectionId = null;
    this._sectionElements = [];
    this._pendingLowerIdx = null;

    this._navOriginalTop = 0;
    this._navOriginalLeft = 0;
    this._navOriginalHeight = 0;

    this.init();
  }

  init() {
    if (this.links.length > 0) {
      this.addEventListener('click', this.handleLinkClick.bind(this));
    }

    if (this.dropdown) {
      this.dropdown.addEventListener('change', this.handleDropdownChange.bind(this));
      this.cacheSectionElements();
    }

    // TTSSM-171: Hide menu items whose target section doesn't exist on the page
    // (e.g., video section hidden because product has no youtube_url metafield)
    this.hideOrphanedItems();

    this.setupStickyBehavior();

    if (this.dropdown) {
      this.syncDropdownToScroll();
    }
  }

  /* ── Sticky ──────────────────────────────────────────────────── */

  get isStuck() {
    return this.nav?.classList.contains('is-stuck') ?? false;
  }

  setupStickyBehavior() {
    if (!this.nav) return;

    this.spacer = document.createElement('div');
    this.spacer.className = 'sub-menu-nav-spacer';
    this.spacer.style.display = 'none';
    this.nav.parentNode.insertBefore(this.spacer, this.nav.nextSibling);

    this.snapshotOriginalPosition();
    this.cachedHeaderHeight = this.getHeaderHeight();

    let ticking = false;
    this.scrollHandler = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.updateSticky();
          this.syncDropdownToScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    let resizeTimer;
    this.resizeHandler = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.cachedHeaderHeight = this.getHeaderHeight();
        this.snapshotOriginalPosition();
        if (this.isStuck) this.applyStuckStyles();
        this.cacheSectionElements();
        this.syncDropdownToScroll();
      }, 150);
    };

    window.addEventListener('scroll', this.scrollHandler, { passive: true });
    window.addEventListener('resize', this.resizeHandler, { passive: true });

    this.updateSticky();
  }

  disconnectedCallback() {
    if (this.scrollHandler) window.removeEventListener('scroll', this.scrollHandler);
    if (this.resizeHandler) window.removeEventListener('resize', this.resizeHandler);
  }

  snapshotOriginalPosition() {
    if (!this.nav) return;

    const ref = this.isStuck && this.spacer ? this.spacer : this.nav;
    const rect = ref.getBoundingClientRect();

    this._navOriginalLeft = rect.left;
    this._navOriginalHeight = this.nav.offsetHeight;
    this._navOriginalTop = rect.top + window.pageYOffset;

    if (this.spacer) {
      this.spacer.style.height = `${this._navOriginalHeight}px`;
    }
  }

  updateSticky() {
    if (!this.nav) return;

    const headerH = this.cachedHeaderHeight || this.getHeaderHeight();
    const stickyLine = headerH + this.STICKY_OFFSET;

    if (this.isStuck) {
      const unstickAt = this._navOriginalTop - stickyLine - this.HYSTERESIS;
      if (window.pageYOffset < unstickAt) {
        this.nav.classList.remove('is-stuck');
        this.clearStuckStyles();
      }
    } else {
      if (this.nav.getBoundingClientRect().top <= stickyLine) {
        this.nav.classList.add('is-stuck');
        this.applyStuckStyles();
      }
    }
  }

  applyStuckStyles() {
    if (!this.nav) return;
    if (window.innerWidth <= 749) {
      this.nav.style.left = '21px';
      this.nav.style.right = '21px';
    } else {
      this.nav.style.left = `${this._navOriginalLeft}px`;
    }
    if (this.spacer) this.spacer.style.display = 'block';
  }

  clearStuckStyles() {
    if (!this.nav) return;
    this.nav.style.left = '';
    this.nav.style.right = '';
    if (this.spacer) this.spacer.style.display = 'none';
  }

  /* ── Link / dropdown clicks ──────────────────────────────────── */

  handleLinkClick(event) {
    const link = event.target.closest('.sub-menu-link');
    if (!link) return;
    event.preventDefault();
    this.scrollToSection(link.getAttribute('data-target-section'));
  }

  handleDropdownChange(event) {
    const id = event.target.value;
    this._activeSectionId = id;
    this.scrollToSection(id);
    const opt = event.target.options[event.target.selectedIndex];
    if (this.dropdownValue && opt) this.dropdownValue.textContent = opt.textContent;
  }

  scrollToSection(targetId) {
    if (!targetId) return;

    const el = document.getElementById(targetId);
    if (!el) {
      console.warn(`Sub Menu: section "${targetId}" not found`);
      return;
    }

    const offset = this.getAnchorOffset();
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.pageYOffset - offset,
      behavior: 'smooth',
    });

    if (history.pushState) history.pushState(null, null, `#${targetId}`);

    if (this.dropdown) {
      this._pendingLowerIdx = null;
      this._activeSectionId = targetId;
      this.dropdown.value = targetId;
      const opt = this.dropdown.options[this.dropdown.selectedIndex];
      if (this.dropdownValue && opt) this.dropdownValue.textContent = opt.textContent;
    }

    if (this.dropdown && 'onscrollend' in window) {
      this._scrollspyPaused = true;
      window.addEventListener(
        'scrollend',
        () => {
          this._scrollspyPaused = false;
          this.syncDropdownToScroll();
        },
        { once: true }
      );
    }
  }

  /* ── Scroll-spy ──────────────────────────────────────────────── */

  getAnchorOffset() {
    if (this.nav) {
      return this.nav.getBoundingClientRect().bottom + this.STICKY_OFFSET + this.SCROLL_PADDING;
    }
    return (this.cachedHeaderHeight || this.getHeaderHeight()) + this.STICKY_OFFSET + this.SCROLL_PADDING;
  }

  cacheSectionElements() {
    if (!this.dropdown) return;
    this._sectionElements = Array.from(this.dropdown.options)
      .map((opt) => ({ id: opt.value, el: document.getElementById(opt.value) }))
      .filter((s) => s.el);

    this._sectionElements.sort((a, b) => {
      const pos = a.el.compareDocumentPosition(b.el);
      if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
      if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
      return 0;
    });
  }

  isAboveSubMenuOrigin() {
    const headerH = this.cachedHeaderHeight || this.getHeaderHeight();
    return window.pageYOffset < this._navOriginalTop - headerH - this.STICKY_OFFSET - this.HYSTERESIS;
  }

  syncDropdownToScroll() {
    if (!this.dropdown || !this.dropdownValue || this._scrollspyPaused) return;
    if (!this._sectionElements.length) return;

    const lineY = this.getAnchorOffset();
    let activeIdx = -1;

    for (let i = 0; i < this._sectionElements.length; i++) {
      if (this._sectionElements[i].el.getBoundingClientRect().top <= lineY) {
        activeIdx = i;
      }
    }

    if (activeIdx === -1) return;

    const prevIdx = this._sectionElements.findIndex((s) => s.id === this._activeSectionId);

    if (activeIdx === 0 && prevIdx > 1 && !this.isAboveSubMenuOrigin()) return;

    if (prevIdx >= 0 && activeIdx < prevIdx) {
      if (this._pendingLowerIdx !== activeIdx) {
        this._pendingLowerIdx = activeIdx;
        return;
      }
      this._pendingLowerIdx = null;
    } else {
      this._pendingLowerIdx = null;
    }

    const id = this._sectionElements[activeIdx].id;
    if (id && id !== this._activeSectionId) {
      this._activeSectionId = id;
      this.dropdown.value = id;
      const opt = this.dropdown.options[this.dropdown.selectedIndex];
      if (opt) this.dropdownValue.textContent = opt.textContent;
    }
  }

  /* ── Helpers ─────────────────────────────────────────────────── */

  getHeaderHeight() {
    const css = getComputedStyle(document.documentElement).getPropertyValue('--header-height').trim();
    if (css) {
      const v = parseFloat(css);
      if (!isNaN(v)) return v;
    }
    const hdr = document.querySelector('#Page-Header, header');
    if (hdr) return hdr.offsetHeight;
    const sec = document.querySelector('.section-header');
    if (!sec) return 0;
    return getComputedStyle(sec).position === 'sticky' ? sec.offsetHeight : 0;
  }

  /**
   * TTSSM-171: Hide menu items (desktop link + mobile option) whose
   * target section ID doesn't exist on the page.
   * This allows sections like video-banner to conditionally render
   * without leaving orphaned submenu items.
   */
  hideOrphanedItems() {
    // Skip in theme editor so editors can still see all items
    if (window.Shopify?.designMode) return;

    this.links.forEach(link => {
      const targetId = link.getAttribute('data-target-section');
      if (targetId && !document.getElementById(targetId)) {
        const listItem = link.closest('.sub-menu-item');
        if (listItem) listItem.style.display = 'none';
      }
    });

    if (this.dropdown) {
      Array.from(this.dropdown.options).forEach(option => {
        const targetId = option.value;
        if (targetId && !document.getElementById(targetId)) {
          option.style.display = 'none';
          option.disabled = true;
        }
      });
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => customElements.define('sub-menu', SubMenu));
} else {
  customElements.define('sub-menu', SubMenu);
}
